import _ from 'lodash'
import { BehaviorSubject } from 'rxjs'
import Client from 'data/_client'
import { ApiResult } from 'data/interface/api'
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import notificationService from 'data/notification'
import { handleReferences } from 'data/lib/handle-references'
import { PromiseWatch } from "./lib/promise-helpers"

export type Multifamily = {
  multifamilyid: number
  propertyid: number
  sortorder: number
  unitdescription: string
  systeminput?: string
}

// private module logic //

const id: string = 'multifamily'
const initialState: ApiResult<Multifamily[]> = { id, state: 'init', value: [] }
const multifamilyStream = new BehaviorSubject<ApiResult<Multifamily[]>>(initialState)
const multifamilyEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let multifamilyMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function clear(): void {
  multifamilyStream.next({ id, state: 'ok', value: [] })
  multifamilyMap = {}
}

function get(mfId: number): Multifamily {
  return multifamilyMap[mfId]
}

function refresh(): Promise<any> {
  const currentValue = multifamilyStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchByPropertyId(propertyid)
}

function fetchOne(multifamilyId: number): Promise<Multifamily | { message: string }> {
  const currentValue = multifamilyStream.getValue().value

  multifamilyStream.next({ id, state: 'busy', value: currentValue })

  return Client.get(`multifamily?multifamilyid=eq.${multifamilyId}`)
    .then((mf: Multifamily[]) => {
      _.forEach(mf, (m) => (multifamilyMap[m.multifamilyid] = m))
      // merge results unique by multifamilyid
      multifamilyStream.next({
        id,
        state: 'ok',
        value: _.unionBy(mf, currentValue, 'multifamilyid')
      })
      return mf[0]
    })
    .catch((err: { message: string }) => {
      multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

function fetchByPropertyId(
  propertyId: number,
  limit: number = 0,
  offset: number = 0,
  noclear = false
): Promise<Multifamily[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  noclear || clear();
  multifamilyStream.next({ id, state: 'busy', value: multifamilyStream.getValue().value })

  let url = `multifamily?propertyid=eq.${propertyId}`
  if (limit && offset >= 0) {
    url = `multifamily?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}`
  }

  return _fetches_.watch(propertyId, Client.get(url)
    .then((mf: Multifamily[]) => {
      _.forEach(mf, (m) => (multifamilyMap[m.multifamilyid] = m))
      // merge results unique by multifamilyid
      multifamilyStream.next({ id, state: 'ok', value: mf })
      return mf
    })
    .catch((err: { message: string }) => {
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    }))
}

function patch(multifamily: {
  multifamilyid: number
}): Promise<Multifamily | { message: string }> {
  const currentValue = multifamilyStream.getValue().value
  const multifamilyid: number = multifamily.multifamilyid
  const currentMF = _.find(currentValue, { multifamilyid })
  const changeset = diff_obj(multifamily, currentMF)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentMF),
    redofunc: () => patch(multifamily)
  })

  // optimistic update
  multifamilyStream.next({
    id,
    state: 'ok',
    value: _.unionBy([multifamily], currentValue, 'multifamilyid')
  })

  return _patches_.watch(multifamilyid, Client.patch(`multifamily?multifamilyid=eq.${multifamilyid}`, changeset)
    .then((mf: Multifamily[]) => {
      _.forEach(mf, (m) => (multifamilyMap[m.multifamilyid] = m))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentMF, 'propertyid')
      if (propertyid) fetchByPropertyId(propertyid)
      return err
    }))
}

function create(propertyid: number): Promise<Multifamily | { message: string }> {
  const currentValue = multifamilyStream.getValue().value
  multifamilyStream.next({ id, state: 'busy', value: currentValue })

  const payload = { propertyid }

  return Client.post(`msg/svc_create_multifamily`, payload)
    .then((mf: Multifamily) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, mf.multifamilyid),
        redofunc: () => create(propertyid)
      })
      multifamilyMap[mf.multifamilyid] = mf
      // merge results unique by multifamilyid
      const currentValue = multifamilyStream.getValue().value.map(m => ({...m, sortorder: m['sortorder'] + 1}))
      multifamilyStream.next({
        id,
        state: 'ok',
        value: _.unionBy([mf], currentValue, 'multifamilyid')
      })
      multifamilyEventStream.next({ type: 'MULTIFAMILY_CREATED', payload: mf })
      return mf
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchByPropertyId(propertyid)
      return err
    })
}

function copy(
  propertyid: number,
  multifamilyid: number
): Promise<Multifamily | { message: string }> {
  const currentValue = multifamilyStream.getValue().value

  const payload = {
    src: 'multifamily',
    setup: 'copy',
    propertyid,
    multifamilyid,
    userId: _.get(JSON.parse(localStorage.getItem('auth.profile') || '{}'), 'user_id'),
    _synchronous: false
  }

  notificationService.notify({
    type: 'info',
    message: 'Multifamily copy initiated; you will be notified upon completion.',
    dismiss: 3000
  })

  return Client.post(`msg/svc_copy_multifamily`, payload)
    .then((mf: Multifamily) => {
      multifamilyMap[mf.multifamilyid] = mf
      const sortorder = mf['sortorder']

      push_undo({
        undofunc: () => deleteOne(propertyid, mf.multifamilyid),
        redofunc: () => copy(propertyid, multifamilyid)
      })

      // merge results unique by multifamilyid and update sortorder
      const currentValue = multifamilyStream.getValue().value
      const value = _(currentValue)
        .unionBy(mf, 'multifamilyid')
        .map((p) =>
          p.sortorder < sortorder || p.multifamilyid == mf.multifamilyid
            ? p
            : { ...p, sortorder: p.sortorder + 1 }
        )
        .value()
      value.forEach((p) => (multifamilyMap[p.multifamilyid] = p))

      multifamilyStream.next({ id, state: 'ok', value })
      multifamilyEventStream.next({ type: 'MULTIFAMILY_CREATED', payload: mf })
      notificationService.notify({
        type: 'info',
        message: 'Multifamily copy complete.',
        dismiss: 3000
      })
      return mf
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchByPropertyId(propertyid)
      return err
    })
}

function copies(
  propertyid: number,
  multifamilyid: number,
  count: number
): Promise<Multifamily[] | { message: string }> {
  const currentValue = multifamilyStream.getValue().value

  const payload = {
    src: 'multifamily',
    count,
    setup: 'copy',
    propertyid,
    multifamilyid,
    userId: _.get(JSON.parse(localStorage.getItem('auth.profile') || '{}'), 'user_id'),
    _synchronous: false
  }

  notificationService.notify({
    type: 'info',
    message: 'Multifamily copy initiated; you will be notified upon completion.',
    dismiss: 3000
  })

  return Client.post(`msg/svc_copy_multifamily`, payload)
    .then(async (mfs: Multifamily[]) => {
      notificationService.notify({
        type: 'info',
        message: 'Multifamily copy complete.',
        dismiss: 3000
      })

      return await fetchByPropertyId(propertyid, 0, 0, true)
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchByPropertyId(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  multifamilyid: number
): Promise<boolean | { message: string }> {
  const currentValue = multifamilyStream.getValue().value
  const sortorder = multifamilyMap[multifamilyid].sortorder

  const deleteFn = () => deleteOne(propertyid, multifamilyid)
  const refreshFn = () => fetchByPropertyId(propertyid)
  if (!(await handleReferences('multifamily', multifamilyid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted multifamilyid and update sortorder
  const value = _(currentValue)
    .filter((p) => p.multifamilyid !== multifamilyid)
    .map((p) => (p.sortorder < sortorder ? p : { ...p, sortorder: p.sortorder - 1 }))
    .value()
  value.forEach((p) => (multifamilyMap[p.multifamilyid] = p))

  multifamilyStream.next({ id, state: 'ok', value })

  const payload = {
    propertyid,
    multifamilyid,
    _synchronous: true
  }

  // update the db
  return _patches_.all(multifamilyid).then(() =>
    Client.post(`msg/svc_delete_multifamily`, payload)
      .then((mf: Multifamily) => {
        delete multifamilyMap[mf.multifamilyid]
        push_undo({
          undofunc: () => {
            Client.post('multifamily', mf)
            multifamilyMap[mf.multifamilyid] = mf
            multifamilyStream.next({ id, state: 'ok', value: currentValue })
            const payload = {
              table: 'multifamily',
              propertyid,
              orders: _(currentValue).orderBy('sortorder').map('multifamilyid').value(),
              _synchronous: true
            }
            Client.post(`msg/svc_reorder`, payload)
          },
          redofunc: () => deleteOne(propertyid, multifamilyid)
        })
        // update already happened optimistically
        return true
      })
      .catch((err: { message: string }) => {
        // on error overwrite optimistic update
        multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
        notificationService.notify({
          type: 'danger',
          message: err.message,
          dismiss: 5000
        })
        fetchByPropertyId(propertyid)
        return err
      })
  )
}

async function deleteMultiple(
  propertyid: number,
  multifamilyids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = multifamilyStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchByPropertyId(propertyid)
  if (!(await handleReferences('multifamily', multifamilyids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted multifamilyid and update sortorder
  const value = _(currentValue)
    .filter((p) => !multifamilyids.includes(p.multifamilyid))
    .orderBy('sortorder')
    .map((p, ix) => ({...p, sortorder: ix+1}))
    .value()
  value.forEach((p) => (multifamilyMap[p.multifamilyid] = p))

  multifamilyStream.next({ id, state: 'ok', value })


  // update the db
  return _patches_.all(multifamilyids).then(() =>
    Promise.all(_.map(multifamilyids, (multifamilyid) => Client.post(`msg/svc_delete_multifamily`, {
      propertyid,
      multifamilyid,
      _synchronous: true
    })
      .then((mf: Multifamily) => {
        delete multifamilyMap[mf.multifamilyid]
        push_undo({
          undofunc: () => {
            Client.post('multifamily', mf)
            multifamilyMap[mf.multifamilyid] = mf
            multifamilyStream.next({ id, state: 'ok', value: currentValue })
            const payload = {
              table: 'multifamily',
              propertyid,
              orders: _(currentValue).orderBy('sortorder').map('multifamilyid').value(),
              _synchronous: true
            }
            Client.post(`msg/svc_reorder`, payload)
          },
          redofunc: () => deleteOne(propertyid, multifamilyid)
        })
        // update already happened optimistically
        return true
      })
      .catch((err: { message: string }) => {
        // on error overwrite optimistic update
        multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
        notificationService.notify({
          type: 'danger',
          message: err.message,
          dismiss: 5000
        })
        fetchByPropertyId(propertyid)
        return err
      })
  ))).then(trues => true)
}

function move(
  propertyid: number,
  multifamilyid: number,
  destination: number
): Promise<boolean | { message: string }> {
  if (!_.isFinite(destination)) return Promise.resolve(false)

  const currentValue = multifamilyStream.getValue().value

  let undo
  const orders = (() => {
    let orders = _(currentValue).orderBy('sortorder').map('multifamilyid').value()
    const current = _.findIndex(orders, (el) => el == multifamilyid)
    undo = {
      undofunc: () => move(propertyid, multifamilyid, current + 1),
      redofunc: () => move(propertyid, multifamilyid, destination)
    }

    const dest = destination - 1
    if (dest < current) {
      orders.splice(dest, 0, multifamilyid)
      orders.splice(current + 1, 1)
    } else {
      orders.splice(dest + 1, 0, multifamilyid)
      orders.splice(current, 1)
    }
    return orders
  })()
  const value = _.map(orders, (id, ix) => {
    multifamilyMap[id] = { ...multifamilyMap[id], sortorder: ix + 1 }
    return multifamilyMap[id]
  })
  multifamilyStream.next({ id, state: 'ok', value })

  const payload = {
    table: 'multifamily',
    propertyid,
    orders,
    _synchronous: true
  }

  // update the db
  return Client.post(`msg/svc_reorder`, payload)
    .then((mf: Multifamily) => {
      // update already happened optimistically
      push_undo(undo)
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      multifamilyStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchByPropertyId(propertyid)
      return err
    })
}

// public module interface //

const multifamilyService = {
  stream: () => multifamilyStream.asObservable(),
  value: () => multifamilyStream.getValue().value,
  eventStream: () => multifamilyEventStream.asObservable(),
  get,
  clear,
  fetchOne,
  fetchByPropertyId,
  patch,
  create,
  copy,
  copies,
  deleteOne,
  deleteMultiple,
  move,
  refresh
}

export default multifamilyService
